import React, { useState, useEffect, useContext } from 'react';
import {
  XMarkIcon,
  TagIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  CheckIcon,
  EyeIcon,
  DocumentTextIcon,
  PlusIcon,
  LinkIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { DocumentIcon as DocumentIconSolid } from '@heroicons/react/24/solid';
import apiClient from '../../utils/apiClient';
import DocumentViewer from '../DocumentViewer';
import { AuthContext } from '../../App';
import ConfirmationModal from '../ConfirmationModal';

interface DocumentDetailModalProps {
  documentId: number;
  onClose: () => void;
  onDelete: (id: number) => void;
  onTagAdd: (docId: number, tag: string) => void;
  onTagDelete: (docId: number, tag: string) => void;
  onAIScan: (docId: number) => void;
  onDownload: (docId: number, fileName: string) => void;
  onDocumentUpdated?: () => void;
  availableTags?: string[];
}

interface LinkedBooking {
  buchung_id: number;
  konto_id: number;
  buchungstag: string;
  name_zahlungsbeteiligter: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number;
  von_konto_id: number | null;
  nach_konto_id: number | null;
}

interface DocumentDetails {
  id: number;
  name: string;
  file_path: string;
  created_at: string;
  member_id: string;
  member_name?: string;
  size: number;
  tag?: string;
  ai_comment?: string;
  invoice_amount?: number;
  is_linked?: number;
  linked_bookings?: LinkedBooking[];
}

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  documentId,
  onClose,
  onDelete,
  onTagAdd,
  onTagDelete,
  onAIScan,
  onDownload,
  onDocumentUpdated,
  availableTags = [],
}) => {
  const { BASE_URL } = useContext(AuthContext);
  const [document, setDocument] = useState<DocumentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (documentId) {
      window.document.body.style.overflow = 'hidden';
      fetchDocumentDetails();
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);

      return () => {
        clearTimeout(timer);
        window.document.body.style.overflow = '';
      };
    }
  }, [documentId]);

  const fetchDocumentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/documents/${documentId}`);
      setDocument(response.data);
      setEditedAmount(response.data.invoice_amount?.toString() || '');
      setEditedName(response.data.name || '');
    } catch (err) {
      console.error('Error fetching document details:', err);
      setError('Dokument konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    window.document.body.style.overflow = '';
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const toggleTag = (tag: string) => {
    if (!document) return;
    const currentTags = document.tag ? document.tag.split(',').map(t => t.trim()).filter(Boolean) : [];
    if (currentTags.includes(tag)) {
      // Remove tag
      const updated = currentTags.filter(t => t !== tag).join(',');
      setDocument({ ...document, tag: updated });
      onTagDelete(document.id, tag);
    } else {
      // Add tag
      const updated = [...currentTags, tag].join(',');
      setDocument({ ...document, tag: updated });
      onTagAdd(document.id, tag);
    }
  };

  const handleAddCustomTag = () => {
    if (!newTag.trim() || !document) return;
    const trimmed = newTag.trim();
    const currentTags = document.tag ? document.tag.split(',').map(t => t.trim()).filter(Boolean) : [];
    if (!currentTags.includes(trimmed)) {
      setDocument({ ...document, tag: [...currentTags, trimmed].join(',') });
      onTagAdd(document.id, trimmed);
    }
    setNewTag('');
    setShowTagPicker(false);
  };

  const handleAIScan = async () => {
    if (!document) return;
    setIsProcessing(true);
    try {
      await onAIScan(document.id);
      await fetchDocumentDetails();
    } catch (err) {
      console.error('Error with AI analysis:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAmount = async () => {
    if (!document) return;
    const amount = parseFloat(editedAmount) || 0;
    try {
      await apiClient.put(`/api/documents/${document.id}`, { invoice_amount: amount });
      setDocument({ ...document, invoice_amount: amount });
      setIsEditingAmount(false);
    } catch (err) {
      console.error('Error updating invoice amount:', err);
    }
  };

  const handleSaveName = async () => {
    if (!document || !editedName.trim()) return;
    const newName = editedName.trim();
    if (newName === document.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await apiClient.put(`/api/documents/${document.id}`, { name: newName });
      setDocument({ ...document, name: newName });
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating document name:', err);
    }
  };

  const formatSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '–';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted.replace('€', 'EUR')}` : `-${formatted.replace('€', 'EUR')}`;
  };

  const tags = document?.tag?.split(',').filter(t => t.trim()) || [];
  const linkedBookings = document?.linked_bookings || [];
  const hasAmount = document?.invoice_amount && document.invoice_amount !== 0;
  const hasAIComment = document?.ai_comment;

  if (!document && !loading) return null;

  return (
    <>
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-primary-600 rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          ) : document ? (
            <>
              {/* Header Card */}
              <div className="p-4">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3">
                  <div className="w-12 h-12 bg-info-100 dark:bg-info-900/50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <DocumentTextIcon className="w-6 h-6 text-info-500 dark:text-info-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate text-sm">
                      {document.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {document.file_path.split('.').pop()?.toUpperCase()} | {formatDate(document.created_at)}
                    </p>
                  </div>
                  {hasAIComment ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 dark:bg-success-900/40 text-success-600 dark:text-success-400">
                      <CheckIcon className="w-3 h-3" />
                      Analysiert
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      Neu
                    </span>
                  )}
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Metadata Row */}
              <div className="px-4 pb-2">
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Hochgeladen von {document.member_name || 'Unbekannt'} | {formatSize(document.size)}
                </p>
              </div>

              {/* Rechnungsbetrag Display */}
              <div className="px-4 py-4 text-center border-b border-gray-200 dark:border-gray-700">
                {isEditingAmount ? (
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={editedAmount}
                      onChange={(e) => setEditedAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveAmount();
                        if (e.key === 'Escape') {
                          setIsEditingAmount(false);
                          setEditedAmount(document.invoice_amount?.toString() || '');
                        }
                      }}
                      className="w-32 px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-xl font-bold text-center text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      autoFocus
                      placeholder="0,00"
                    />
                    <button
                      onClick={handleSaveAmount}
                      className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                    >
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingAmount(false);
                        setEditedAmount(document.invoice_amount?.toString() || '');
                      }}
                      className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : hasAmount ? (
                  <>
                    <p className={`text-3xl font-bold ${document.invoice_amount! >= 0 ? 'text-success-500' : 'text-error-500'}`}>
                      {formatAmount(document.invoice_amount!)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <span className="inline-flex items-center gap-1">
                        {hasAIComment ? (
                          <>
                            <SparklesIcon className="w-3.5 h-3.5 text-primary-500" />
                            <span className="text-primary-500">Von KI erkannt</span>
                          </>
                        ) : (
                          <>
                            <PencilIcon className="w-3.5 h-3.5" />
                            <span>Manuell eingetragen</span>
                          </>
                        )}
                        <button
                          onClick={() => setIsEditingAmount(true)}
                          className="ml-2 text-primary-500 hover:text-primary-600"
                        >
                          Bearbeiten
                        </button>
                      </span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-400 dark:text-gray-500">Kein Betrag</p>
                    <button
                      onClick={() => setIsEditingAmount(true)}
                      className="mt-2 text-sm text-primary-500 hover:text-primary-600 font-medium inline-flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Betrag eintragen
                    </button>
                  </>
                )}
              </div>

              {/* Content Sections - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-5">

                {/* DOKUMENTDETAILS Section */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    <InformationCircleIcon className="w-4 h-4" />
                    DOKUMENTDETAILS
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 group">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Dateiname</span>
                      {isEditingName ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveName();
                              if (e.key === 'Escape') {
                                setIsEditingName(false);
                                setEditedName(document.name || '');
                              }
                            }}
                            className="w-40 px-2 py-1 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            autoFocus
                          />
                          <button
                            onClick={handleSaveName}
                            className="p-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingName(false);
                              setEditedName(document.name || '');
                            }}
                            className="p-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className="flex items-center gap-1.5 text-sm text-gray-900 dark:text-white font-medium truncate max-w-[200px] hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                          <span className="truncate">{document.name}</span>
                          <PencilIcon className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-gray-400 flex-shrink-0 transition-opacity" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Hochgeladen am</span>
                      <span className="text-sm text-gray-900 dark:text-white">{formatDate(document.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Dateigröße</span>
                      <span className="text-sm text-gray-900 dark:text-white">{formatSize(document.size)}</span>
                    </div>
                  </div>
                </div>

                {/* TAGS Section */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    <TagIcon className="w-4 h-4" />
                    TAGS ({tags.length})
                  </div>
                  {/* Active tags + add button */}
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                      >
                        <CheckIcon className="w-3 h-3" />
                        {tag.trim()}
                        <button
                          onClick={() => toggleTag(tag.trim())}
                          className="ml-0.5 text-primary-400 hover:text-error-500 transition-colors"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => setShowTagPicker(!showTagPicker)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                        showTagPicker
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                          : 'text-gray-500 dark:text-gray-400 border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <PlusIcon className={`w-3 h-3 transition-transform ${showTagPicker ? 'rotate-45' : ''}`} />
                      Tags
                    </button>
                  </div>
                  {/* Tag picker (shown on click) */}
                  {showTagPicker && (
                    <div className="mt-2 p-2.5 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
                      {availableTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {availableTags.filter(t => !tags.map(x => x.trim()).includes(t)).map(tag => (
                            <button
                              key={tag}
                              onClick={() => toggleTag(tag)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 dark:hover:border-primary-700 transition-colors"
                            >
                              <PlusIcon className="w-3 h-3" />
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Custom tag input */}
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddCustomTag();
                            if (e.key === 'Escape') {
                              setShowTagPicker(false);
                              setNewTag('');
                            }
                          }}
                          placeholder="Neuer Tag..."
                          className="flex-1 px-2.5 py-1 text-xs text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                        />
                        {newTag.trim() && (
                          <button onClick={handleAddCustomTag} className="p-1.5 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors">
                            <CheckIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* VERWEISENDE BUCHUNGEN Section */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    <LinkIcon className="w-4 h-4" />
                    VERWEISENDE BUCHUNGEN ({linkedBookings.length})
                  </div>
                  {linkedBookings.length > 0 ? (
                    <div className="space-y-2">
                      {linkedBookings.map((booking) => (
                        <div
                          key={booking.buchung_id}
                          className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            booking.betrag >= 0
                              ? 'bg-success-100 dark:bg-success-900/50'
                              : 'bg-error-100 dark:bg-error-900/50'
                          }`}>
                            <CurrencyDollarIcon className={`w-5 h-5 ${
                              booking.betrag >= 0 ? 'text-success-500' : 'text-error-500'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {booking.name_zahlungsbeteiligter || booking.buchungstext}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(booking.buchungstag)} | {formatAmount(booking.betrag)}
                            </p>
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-500 text-center py-3">
                      Keine Buchungen verweisen auf dieses Dokument
                    </p>
                  )}
                </div>

                {/* KI-ANALYSE Section */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    <SparklesIcon className="w-4 h-4" />
                    KI-ANALYSE
                    {hasAIComment ? (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 dark:bg-success-900/40 text-success-600 dark:text-success-400">
                        Abgeschlossen
                      </span>
                    ) : (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        Nicht gestartet
                      </span>
                    )}
                  </div>
                  {isProcessing ? (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin"></div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 dark:text-gray-300">Dokument wird verarbeitet...</p>
                          <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-primary-500 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : hasAIComment ? (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-sm text-gray-700 dark:text-gray-300">{document.ai_comment}</p>
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Konfidenz:</span>
                          <span className="text-xs text-success-500 font-medium">98%</span>
                        </div>
                        <button
                          onClick={handleAIScan}
                          className="text-xs text-primary-500 hover:text-primary-600 font-medium inline-flex items-center gap-1"
                        >
                          <SparklesIcon className="w-3.5 h-3.5" />
                          Erneut analysieren
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleAIScan}
                      className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <SparklesIcon className="w-5 h-5" />
                      Dokument analysieren
                    </button>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button
                  onClick={() => setShowViewer(true)}
                  className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <EyeIcon className="w-5 h-5" />
                  Vorschau
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="py-3 px-4 bg-error-100 dark:bg-error-900/30 hover:bg-error-200 dark:hover:bg-error-900/50 text-error-600 dark:text-error-400 rounded-xl font-medium transition-colors flex items-center justify-center"
                  title="Dokument löschen"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleClose}
                  className="py-3 px-6 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors"
                >
                  Schließen
                </button>
              </div>
            </>
          ) : null}
      </div>
    </div>

      {/* Document Viewer */}
      {document && showViewer && (
        <DocumentViewer
          documentId={document.id}
          fileName={document.name}
          filePath={document.file_path}
          onClose={() => setShowViewer(false)}
          onDocumentUpdated={onDocumentUpdated}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        type="warning"
        title="Löschen bestätigen"
        message={`Möchten Sie das Dokument "${document?.name || 'dieses Dokument'}" wirklich löschen?`}
        variant="two"
        showCloseButton={true}
        onClose={() => setShowDeleteModal(false)}
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setShowDeleteModal(false)
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              onDelete(document?.id || documentId);
              setShowDeleteModal(false);
              handleClose();
            }
          }
        }}
      />
    </>
  );
};

export default DocumentDetailModal;
